import type { BaseRecord, HttpError } from '@refinedev/core'
import type { ChangeEvent, KeyboardEvent } from 'react'

export type RefineMutationError = HttpError | null

export type TApiResponse<T = unknown> = {
    data: T
    total?: number
    success?: boolean
    message?: string
}

export type TPaginationParams = {
    page: number
    per_page: number
}

export type TSortParams = {
    field: string
    order: 'asc' | 'desc'
    sort_by?: string
    sort_order?: 'asc' | 'desc'
}

export type TFilterParams = {
    [key: string]: string | number | boolean
}

export type TCustomError = Error & {
    status: number
    statusCode: number
    data?: TApiErrorResponse
}

export type TApiErrorResponse = {
    message?: string
    error?: string
    errors?: Record<string, string[]>
    [key: string]: unknown
}

export type TApiListResponse<TData extends BaseRecord> = {
    data: TData[]
    total: number
}

export type TApiSingleResponse<TData extends BaseRecord> = {
    data: TData
}

export type TApiSuccessResponse = {
    success: boolean
    message?: string
}

export type TFile = {
    id: number
    requestId: number
    name: string
    signedUrl: string
    requestSongId?: number | null
    commentId?: number | null
}

export type TDistributionDetails = {
    id: number
    requestId: number
    cartItemTypeId: number
    productType: number | null
    distributionMethod: number | null
    numberOfUnits: number | null
    currency: number | null
    retailPrice: number | null
    publicationType: string | null
    publicationTypeOther?: string | null
    totalNumberOfWorks: number | null
    numberOfCopyrightedWorks: number | null
    numberOfWorks: number | null
    editableFields?: TEditableField[]
}

export type TCopyrightPublisherShareFields = {
    printPublisherShare: number | null
    digitalPublisherShare: number | null
}

export type TCopyrightShareVisibility = {
    showPrintShare: boolean
    showDigitalShare: boolean
}

export type TEditableField = {
    key: string
    kind: 'text' | 'date' | 'boolean' | 'number' | 'select'
    options?: { id: number; name: string }[]
    value?: string | number | null
}

export type TSong = {
    id: number
    requestId: number
    publisherFee: number | null
    statusId: number
    title: string
    writers: string
    artist?: string | null
    additionalComments: string | null
    copyrightNotice: string | null
    digitalPublisherCurrency: number | null
    digitalPublisherShare: number | null
    digitalPublisherFee: number | null
    printPublisherCurrency: number | null
    printPublisherShare: number | null
    printPublisherFee: number | null
    lyricsPublisherFee: number | null
    lyricsPublisherCurrency: number | null
    baSongNumber?: number
    baFeeTypeId?: number
    baPermissionFee?: number | null
    baProrate?: number | null
    baProratePercentage?: number | null
    baProrateOriginalFee?: number | null
    baInternalApprovalId?: number | null
    baExternalApprovalId?: number | null
    baResponseChosen?: string | null
    baPaid?: number
    baTerritory?: string | null
    organizationId: number | null
    deletedAt?: string | null
    deletedByPublisherRequestId?: number | null
    clientPrice?: string | number | null
    calculatedPrice?: string | null
    rightsOutcome?: string | null
    rightsSource?: string | null
    catalogSongId?: string | null
    bindingSource?: string | null
    cartPosition?: number | null
    priceSource?: string | null
    copyrightNoticeSource?: string | null
    copyrightNoticeSongId?: number | null
    payoutAmount?: string | null
    payoutCurrency?: string | null
    payoutSource?: string | null
    owners?: {
        organizationId: number | null
        publisherName: string | null
        sharePct: string
        source: string
    }[]
    editableFields?: TEditableField[]
    // Publisher-specific fields
    counterOfferFees?: {
        requestSongId: number
        requestId: number
        counterOfferType: number
        initialFee: number
        proposedFee: number
        currencyId: number
        createdDate: string
    }[]
    approvalDecision?: {
        requestSongId: number
        requestId: number
        isApproved: number
        rejectReason?: string
        rejectDescription?: string | null
        comment?: string | null
        createdAt: string
    }
}

export type TRegionItem = {
    id: number
    isExcluded: number
}

export type TRegionData = {
    territories: TRegionItem[]
    countries: TRegionItem[]
}

export type TClientRequest = {
    id: number
    createdAt: string
    cartItemTypeId: number
    statusId: number
    regionId: number | null
    licenseeName: string
    publicationTitle: string
    publicationDescription: string | null
    isAcademicJournalOrMagazine: string | number | null
    publicationAuthorArrangerEditor: string
    isReprint: number
    publisherName: string
    publicationType: string
    plannedPublicationDate: string
    /** Both are worked out on the way out — from the region and from the lines the request states. */
    distributionTerritory: string
    distributionMethod: number | null
    additionalComments: string | null
    datesOfPerformance?: string[] | null
    // Expands
    songs?: TSong[]
    files?: TFile[]
    distributionDetails?: TDistributionDetails[]
}

export type TEnumItem = {
    id: number
    name: string
    enumName: string
}

export type TOrganization = {
    id: number
    name: string
}

export type TUser = {
    id: number
    username: string
    organizationId?: number | null
}

// Roles
export const ROLES = {
    ADMIN_PANEL_PUBLISHER: 'adminPanelPublisher',
    ADMIN_PANEL_COPYRIGHT_MANAGER: 'adminPanelCopyrightManager',
    ADMIN_PANEL_SHARED_REQUEST_USER: 'adminPanelSharedRequestUser',
    ADMIN_PANEL_ADMIN: 'rootAdmin',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

// Authentication form types
export type TLoginFormData = {
    email: string
    password: string
}

export type TRegisterFormData = {
    email: string
    password: string
    confirmPassword: string
    firstName: string
    lastName: string
}

export type TForgotPasswordFormData = {
    email: string
}

export type TResetPasswordFormData = {
    password: string
    confirmPassword: string
    token: string
}

export type TSongItemType = {
    id: number
    title: string
    status: string
    price: number
    comment?: string
    authors?: string
}

export type TSongStatus = {
    status: 'approved' | 'rejected' | 'counteroffer' | null
    reason: string
    counterOfferFee?: number
}

export type TStatusHandlers = {
    onApprove: (id: number) => void
    onReject: (id: number) => void
    onChangeStatus: (id: number) => void
}

export type TSongSharesHandlers = {
    shares: { US: number; Europe: number }
    editing: 'US' | 'Europe' | null
    inputValue: string
    onEditClick: (territory: 'US' | 'Europe') => void
    onInputChange: (e: ChangeEvent<HTMLInputElement>) => void
    onInputBlur: (territory: 'US' | 'Europe') => void
    onInputKeyDown: (e: KeyboardEvent, territory: 'US' | 'Europe') => void
    onEditShare: (territory: 'US' | 'Europe', value: number) => void
}

export type TProcessingAction = 'approve' | 'reject' | 'reset' | null

// Common UI/Context types
export type TEnumContextType = {
    enums: TEnumItem[]
    loading: boolean
    error: string | null
}

export type TUseCanAccessProps = {
    fallbackRedirect?: string
    allowedRoles?: string[]
    resource?: string
    action?: string
}

export type TAuthData = {
    authenticated: boolean
    error?: Error | { message: string } | string
    user?: {
        id: string | number
        type?: string
    }
    roles?: string[]
}
